import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TextField,
  CircularProgress,
  FormControl,
  TableContainer,
  Autocomplete,
  InputAdornment,
  Grid,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import BreadcrumbNav from "../components/BreadcrumbNav";
import { windowsTableStyles } from "../styles/windowsTableStyles";
import { textFieldStyles } from "../styles/textFieldStyles";
import axios from "../utils/axiosConfig";
import CustomAutocomplete from "../components/CustomAutocomplete";
import { createStatusTranslator } from "../utils/statusTranslation";

const ActivityLoggingPage = () => {
  const { t } = useTranslation(['pages', 'common']);
  const breadcrumbs = [{ label: t('navigation:home'), href: "/" }, { label: t('pages:activity.title') }];

  const tableStyles = windowsTableStyles();

  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [actionFilter, setActionFilter] = useState(null);
  const [userFilter, setUserFilter] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [users, setUsers] = useState([]);
  const [delayedCheck, setDelayedCheck] = useState(false);

  // All possible actions that could happen in the activity log
  // (If you need to update this list, update both here and in the backend if needed)
  const actionTypes = [
    { value: "all", label: t('pages:activity.all_actions') },
    { value: "timesheet_created", label: t('pages:activity.timesheet_created') },
    { value: "timesheet_updated", label: t('pages:activity.timesheet_updated') },
    { value: "timesheet_deleted", label: t('pages:activity.timesheet_deleted') },
    { value: "email_sent", label: t('pages:activity.email_sent') },
    { value: "relationship_created", label: t('pages:activity.relationship_created') },
    { value: "relationship_updated", label: t('pages:activity.relationship_updated') },
    { value: "relationship_deleted", label: t('pages:activity.relationship_deleted') },
    { value: "document_uploaded", label: t('pages:activity.document_uploaded') },
    { value: "document_updated", label: t('pages:activity.document_updated') },
    { value: "document_deleted", label: t('pages:activity.document_deleted') },
    { value: "file_created", label: t('pages:activity.file_created') },
    { value: "file_updated", label: t('pages:activity.file_updated') },
    { value: "file_deleted", label: t('pages:activity.file_deleted') },
    { value: "task_created", label: t('pages:activity.task_created') },
    { value: "task_updated", label: t('pages:activity.task_updated') },
    { value: "task_deleted", label: t('pages:activity.task_deleted') },
    { value: "user_login", label: t('pages:activity.user_login') },
    { value: "user_logout", label: t('pages:activity.user_logout') },
    { value: "disbursement_created", label: t('pages:activity.disbursement_created') },
    { value: "disbursement_updated", label: t('pages:activity.disbursement_updated') },
    { value: "disbursement_deleted", label: t('pages:activity.disbursement_deleted') },
    { value: "appointment_created", label: t('pages:activity.appointment_created') },
    { value: "appointment_updated", label: t('pages:activity.appointment_updated') },
    { value: "appointment_deleted", label: t('pages:activity.appointment_deleted') },
    // Add any additional actions below as needed:
    { value: "credit_created", label: t('pages:activity.credit_created') },
    { value: "credit_updated", label: t('pages:activity.credit_updated') },
    { value: "credit_deleted", label: t('pages:activity.credit_deleted') },
    { value: "invoice_created", label: t('pages:activity.invoice_created') },
    { value: "invoice_updated", label: t('pages:activity.invoice_updated') },
    { value: "invoice_deleted", label: t('pages:activity.invoice_deleted') },
    { value: "provision_created", label: t('pages:activity.provision_created') },
    { value: "provision_updated", label: t('pages:activity.provision_updated') },
    { value: "provision_deleted", label: t('pages:activity.provision_deleted') },
    { value: "note_created", label: t('pages:activity.note_created') },
    { value: "note_updated", label: t('pages:activity.note_updated') },
    { value: "note_deleted", label: t('pages:activity.note_deleted') },
    { value: "folder_created", label: t('pages:activity.folder_created') },
    { value: "folder_updated", label: t('pages:activity.folder_updated') },
    { value: "folder_deleted", label: t('pages:activity.folder_deleted') },
    { value: "email_received", label: t('pages:activity.email_received') },
    { value: "login_failed", label: t('pages:activity.login_failed') },
    { value: "password_changed", label: t('pages:activity.password_changed') },
    { value: "user_created", label: t('pages:activity.user_created') },
    { value: "user_updated", label: t('pages:activity.user_updated') },
    { value: "user_deleted", label: t('pages:activity.user_deleted') },
    { value: "profile_updated", label: t('pages:activity.profile_updated') },
    { value: "settings_updated", label: t('pages:activity.settings_updated') },
    { value: "report_generated", label: t('pages:activity.report_generated') },
    { value: "export_performed", label: t('pages:activity.export_performed') },
    { value: "import_performed", label: t('pages:activity.import_performed') },
    { value: "reminder_sent", label: t('pages:activity.reminder_sent') },
    { value: "reminder_received", label: t('pages:activity.reminder_received') },
    { value: "timer_started", label: t('pages:activity.timer_started') },
    { value: "timer_stopped", label: t('pages:activity.timer_stopped') },
    { value: "timer_paused", label: t('pages:activity.timer_paused') },
    { value: "timer_resumed", label: t('pages:activity.timer_resumed') },
    // ...add more as your application grows
  ];

  const entityTranslationMap = {
    'Timesheet': 'pages:timesheets.title',
    'Email': 'common:email',
    'Relationship': 'pages:relationships.title',
    'Document': 'pages:documents.title',
    'File': 'pages:files.title',
    'Task': 'pages:tasks.title',
    'User': 'common:user',
    'Disbursement': 'pages:disbursements.title',
    'Appointment': 'pages:appointments.title',
    'Other': 'common:other'
  };

  const getTranslatedAction = (action) => {
    const found = actionTypes.find((type) => type.value === action);
    return found
      ? found.label
      : action.replace(/_/g, ' ').toUpperCase();
  };

  useEffect(() => {
    fetchActivityLogs();
    fetchUsers();
  }, [page, rowsPerPage, actionFilter, userFilter, startDate, endDate, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDelayedCheck(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const fetchActivityLogs = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };

      // Add filters if they exist
      if (actionFilter && actionFilter.value && actionFilter.value !== 'all') {
        params.action = actionFilter.value;
      }
      if (userFilter && userFilter._id) {
        params.user = userFilter._id;
      }
      if (startDate) {
        params.startDate = new Date(startDate).toISOString();
      }
      if (endDate) {
        params.endDate = new Date(endDate).toISOString();
      }

      const response = await axios.get("/api/activity-logs", { params });
      setActivityLogs(response.data.activityLogs);
      setTotalCount(response.data.total);
    } catch (error) {
      console.error("Error fetching activity logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get("/api/users");
      // Transform user data to match CustomAutocomplete expectations
      const transformedUsers = response.data.map(user => ({
        ...user,
        type: "Lawyer", // Set type to "Lawyer" for CustomAutocomplete filtering
        contractType: "Individual", // Set contractType for proper display
        businessName: null // Ensure businessName is null for individual users
      }));
      setUsers(transformedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePageChange = (_event, newPage) => {
    setPage(newPage);
  };

  const getActionColor = (action) => {
    if (action.includes('created')) return { backgroundColor: '#85bc20', color: 'black' }; // Green
    if (action.includes('updated')) return { backgroundColor: '#2196f3', color: 'white' }; // Blue
    if (action.includes('deleted')) return { backgroundColor: '#e5202e', color: 'white' }; // Red
    if (action.includes('login')) return { backgroundColor: '#9c27b0', color: 'white' }; // Purple
    if (action.includes('logout')) return { backgroundColor: '#ff9800', color: 'black' }; // Orange
    if (action.includes('email')) return { backgroundColor: '#00bcd4', color: 'black' }; // Cyan
    if (action.includes('document') || action.includes('file')) return { backgroundColor: '#4caf50', color: 'black' }; // Light Green
    return { backgroundColor: '#9e9e9e', color: 'white' }; // Gray for default
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // Helper to translate entityType using i18n keys if available
  const translateEntityType = (entityType) => {
    if (!entityType) return "-";
    const key = entityTranslationMap[entityType];
    if (key) {
      return t(key);
    }
    return entityType;
  };

  // Filter activity logs based on search term
  const filteredActivityLogs = activityLogs?.filter((log) => {
    const normalizeString = (str) => str?.replace(/\s+/g, "").toLowerCase();

    const matchesSearchTerm = searchTerm
      ? normalizeString(log.action).includes(normalizeString(searchTerm)) ||
        normalizeString(log.description).includes(normalizeString(searchTerm)) ||
        (log.user &&
          normalizeString(
            `${log.user?.firstName || ""} ${log.user?.lastName || ""}`
          ).includes(normalizeString(searchTerm))) ||
        normalizeString(log.entityType || "").includes(normalizeString(searchTerm))
      : true;

    return matchesSearchTerm;
  }) || [];

  return (
    <Container
      maxWidth="lg"
      sx={{
        backgroundColor: "white",
        boxShadow: "0px 4px 16px rgba(0,0,0,0.013)",
        pt: 2,
        marginBottom: 8,
        marginTop: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          mt: 2,
        }}
      >
        <BreadcrumbNav title={t('pages:activity.title')} breadcrumbs={breadcrumbs} />
      </Box>

      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              marginBottom: 2,
            }}
          >
            <TextField
              label={t('common:search')}
              value={searchTerm}
              size="small"
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: {
                  padding: "0 10px",
                  boxShadow: "none",
                },
              }}
              sx={textFieldStyles({ width: "20%", marginRight: "0" })}
            />
            <FormControl sx={{ width: "20%" }} size="small">
              <CustomAutocomplete
                options={users}
                value={userFilter}
                onChange={(_event, newValue) => {
                  setUserFilter(newValue);
                }}
                label={t('pages:activity.filter_by_user')}
                filterType="Member"
                addNewLabel={t('pages:activity.add_user')}
                addNewPath="/relationships/create"
                showIcon={true}
                icon={FilterListIcon}
                showAddNewOption={false}
              />
            </FormControl>
            <Autocomplete
              options={actionTypes}
              value={actionFilter}
              onChange={(_event, newValue) => setActionFilter(newValue)}
              getOptionLabel={(option) => option.label}
              sx={{ width: "20%" }}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('pages:activity.filter_by_status')}
                  sx={textFieldStyles({ width: "100%", marginRight: "0" })}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon />
                      </InputAdornment>
                    ),
                    sx: {
                      padding: "0 10px",
                      boxShadow: "none",
                    },
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            />
            <TextField
              label={t('forms:labels.startDate')}
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              sx={textFieldStyles({ width: "20%", marginRight: "0" })}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                sx: {
                  padding: "0 10px",
                  boxShadow: "none",
                },
              }}
            />
            <TextField
              label={t('forms:labels.endDate')}
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              sx={textFieldStyles({ width: "20%", marginRight: "0" })}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                sx: {
                  padding: "0 10px",
                  boxShadow: "none",
                },
              }}
            />
          </Box>
        </Grid>
      </Grid>

      <Box>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: "32px 24px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            {delayedCheck && filteredActivityLogs?.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "32px 24px",
                }}
              >
                {t('common:no_data_available')}
              </Box>
            ) : (
              <Table sx={tableStyles.container} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableStyles.headerCell}>{t('pages:activity.date_time')}</TableCell>
                    <TableCell sx={tableStyles.headerCell}>{t('common:user')}</TableCell>
                    <TableCell sx={{ ...tableStyles.headerCell, width: '120px' }}>{t('pages:activity.action')}</TableCell>
                    <TableCell sx={tableStyles.headerCell}>{t('common:description')}</TableCell>
                    <TableCell sx={tableStyles.headerCell}>{t('pages:activity.entity_type')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredActivityLogs.map((log) => (
                    <TableRow key={log._id} sx={tableStyles.row}>
                      <TableCell sx={tableStyles.cell}>
                        {formatDate(log.createdAt)}
                      </TableCell>
                      <TableCell sx={tableStyles.cell}>
                        {log.user ? `${log.user.firstName} ${log.user.lastName}` : t('common:unknown_user')}
                      </TableCell>
                      <TableCell sx={{ ...tableStyles.cell, width: '120px' }}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "1px 6px",
                            borderRadius: "20px",
                            backgroundColor: getActionColor(log.action).backgroundColor,
                            color: getActionColor(log.action).color,
                            fontSize: "8.25px",
                            fontFamily:
                              '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                            fontWeight: "700",
                            textTransform: "upperCase",
                            height: "12px",
                            letterSpacing: "0.2em",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            lineHeight: "12px",
                          }}
                        >
                          {getTranslatedAction(log.action)}
                        </span>
                      </TableCell>
                      <TableCell sx={tableStyles.cell}>
                        {log.description}
                      </TableCell>
                      <TableCell sx={tableStyles.cell}>
                        {translateEntityType(log.entityType)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ borderTop: "1px solid #ddd", padding: "10px" }}
              labelRowsPerPage={t('common:rows_per_page')}
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} ${t('common:of')} ${count !== -1 ? count : `${t('common:more_than')} ${to}`}`
              }
              getItemAriaLabel={(type) => {
                if (type === 'first') {
                  return t('common:go_to_first_page');
                }
                if (type === 'last') {
                  return t('common:go_to_last_page');
                }
                if (type === 'next') {
                  return t('common:go_to_next_page');
                }
                return t('common:go_to_previous_page');
              }}
            />
          </TableContainer>
        )}
      </Box>
    </Container>
  );
};

export default ActivityLoggingPage;
